import React, { useState } from 'react';
import { X, Hash, Users, MapPin, FileText } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, Table } from '../../services/dataService';

interface EditTableModalProps {
  table: Table;
  onClose: () => void;
  onSuccess: () => void;
}

const EditTableModal: React.FC<EditTableModalProps> = ({ table, onClose, onSuccess }) => {
  const { tenantData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    tableNumber: table.tableNumber || '',
    name: table.name || '',
    description: table.description || '',
    capacity: table.capacity ? table.capacity.toString() : '',
    location: table.location || '',
    notes: table.notes || '',
    status: table.status,
    isActive: table.isActive
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.tableNumber.trim()) {
      setError('Table number is required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Check if table number already exists (excluding current table)
      if (formData.tableNumber.trim() !== table.tableNumber) {
        const existingTable = await dataService.getTableByNumber(tenantData!.id, formData.tableNumber.trim());
        if (existingTable && existingTable.id !== table.id) {
          setError('Table number already exists');
          return;
        }
      }

      const updateData = {
        tableNumber: formData.tableNumber.trim(),
        name: formData.name.trim() || undefined,
        description: formData.description.trim() || undefined,
        capacity: formData.capacity ? parseInt(formData.capacity) : undefined,
        location: formData.location.trim() || undefined,
        notes: formData.notes.trim() || undefined,
        status: formData.status as Table['status'],
        isActive: formData.isActive
      };

      await dataService.updateTable(tenantData!.id, table.id!, updateData);
      onSuccess();
    } catch (error) {
      console.error('Error updating table:', error);
      setError('Failed to update table. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Edit Table</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Table Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Table Number *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Hash className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                name="tableNumber"
                value={formData.tableNumber}
                onChange={handleInputChange}
                className="input-field pl-10"
                placeholder="e.g., T1, A-01, Table 1"
                required
              />
            </div>
          </div>

          {/* Table Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Table Name (Optional)
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="input-field"
              placeholder="e.g., Window Table, VIP Table"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="input-field"
            >
              <option value="available">Available</option>
              <option value="occupied">Occupied</option>
              <option value="reserved">Reserved</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>

          {/* Capacity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Capacity (Optional)
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Users className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="number"
                name="capacity"
                value={formData.capacity}
                onChange={handleInputChange}
                className="input-field pl-10"
                placeholder="Number of seats"
                min="1"
                max="20"
              />
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location (Optional)
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MapPin className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="input-field pl-10"
                placeholder="e.g., Near window, Corner, Balcony"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description (Optional)
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="input-field"
              placeholder="Additional details about the table..."
              rows={3}
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (Optional)
            </label>
            <div className="relative">
              <div className="absolute top-3 left-3 pointer-events-none">
                <FileText className="h-4 w-4 text-gray-400" />
              </div>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="input-field pl-10"
                placeholder="Internal notes..."
                rows={2}
              />
            </div>
          </div>

          {/* Active Status */}
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="isActive"
              name="isActive"
              checked={formData.isActive}
              onChange={handleInputChange}
              className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
            />
            <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
              Table is active
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Update Table'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTableModal; 